import { UIIcon, UIIconProps } from 'dig-components/dist/icons';
import { useEventListener, useForceUpdate } from 'hellospa/common/hooks';
import React from 'react';
import ReactDOM from 'react-dom';

/**
 * PanelComponent will be rendered whether it's active or not. If the
 * PanelComponent is expensive to render, you might check the `isOpen` and
 * `return null`.
 *
 * If your new panel needs to use a badge, but shouldn't render when closed then
 * I'd try to separate those two. Point directly to a component that uses
 * whatever hooks it needs to render the badge. Then that component can do
 * something like `return isOpen ? <YourUIComponent /> : null`
 *
 *
 */
export type PanelComponent = React.ComponentType<{
  setBadgeState: (badgeState: BadgeState) => void
  badgeState: BadgeState
  isOpen: boolean
}>
type BadgeState = null | 'red' | 'yellow' | 'green'


type Props = {
  title: string,
  icon: UIIconProps['src']
  Component: PanelComponent
}

/**
 * DebugPanel is responsible for figuring out whether the debug toolbar is
 * present. If it is, it's responsible for creating the new panel and managing
 * the DOM/React interactions.
 *
 * The backend implementation of the toolbar attaches its tools to its root
 * element. If the FE/BE are out of sync and these don't appear to match, this
 * will refuse to render the new panel.
 */
export const DebugPanel = (({
  title,
  icon,
  Component,
}) => {
  const forceUpdate = useForceUpdate();
  const liRef = React.useRef<HTMLLIElement>();
  const [badge, setBadge] = React.useState<BadgeState>(null);


  const [isOpen, setIsOpen] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);
  useEventListener(panelRef.current, 'hs-toggle-debug-panel', (event) => {
    if (event instanceof CustomEvent) {
      const panelVisible = Boolean(event.detail?.panelVisible);
      setIsOpen(panelVisible);
    }
  });


  /**
   * I'mm using Partial<ToolbarAPI> to force the code to verify all the
   * functions exist. This'll be helpful if these get out of sync because the
   * component will return early instead of crashing while trying to call a
   * function that doesn't exist.
   */
  type DebugToolsRoot = HTMLElement & Partial<ToolbarAPI>;
  const debugToolsRoot = document.getElementById('hs-debug-tools') as DebugToolsRoot | null;
  React.useEffect(() => {
    if (debugToolsRoot && !debugToolsRoot.buttonHandler) {
      // Depending on where the debug-panel is used, you can end up here before
      // the debug toolbar's code has run.
      setTimeout(forceUpdate, 1000);
    }
  } /* run on every render */);

  React.useEffect(() => {
    let li = liRef.current;
    if (!li && debugToolsRoot) {
      const ul = debugToolsRoot.querySelector('.hs-debug-tools__toolbar > ul');
      if (ul) {
        li = document.createElement('li');
        ul.appendChild(li);
        liRef.current = li;
        forceUpdate();
        return () => {
          ul.removeChild(li!);
        };
      }
    }
    return () => { };
  }, [debugToolsRoot, forceUpdate]);


  if (!debugToolsRoot) {
    return null;
  }
  const { buttonHandler } = debugToolsRoot;
  if (!buttonHandler) {
    // eslint-disable-next-line no-console
    console.warn('hs-debug-tools was found, but is missing buttonHandler');
    return null;
  }


  const li = liRef.current;

  if (!li) {
    return null;
  }
  return ReactDOM.createPortal((
    <React.Fragment>
      <button
        title={title}
        onClick={buttonHandler}
        className="foundation-exempt"
      >
        <UIIcon width={32} height={32} src={icon} />
        {badge && (
          <div className={`hs-debug-tools__toolbar-button-count ${badge}`} />
        )}
      </button>
      <div className="hs-debug-tools__panel" ref={panelRef} >
        <div className="hs-debug-tools__section">
          <Component
            badgeState={badge}
            isOpen={isOpen}
            setBadgeState={setBadge} />
        </div>
      </div>
    </React.Fragment>
  ), li);
}) as DebugPanelWithSubComponents;


/**
 * https://github.com/HelloFax/HelloFax/blob/e6ca69b3e04b876e2ff59fb9938d193f006906e8/templates/es6/_debug_tools.php#L1108-L1110
 */
type ToolbarAPI = {
  buttonHandler: () => void
}
type DebugPanelWithSubComponents = React.FC<Props> & SubComponents


const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="hs-debug-tools__section-header">
    <h2>{children}</h2>
  </div>
);
type SubComponents = {
  SectionHeader: typeof SectionHeader
}
DebugPanel.SectionHeader = SectionHeader;
